import React, { useState, useEffect, useCallback, useRef } from "react";
import { API } from "../../services/api";
import { useAuth } from "../../auth/AuthContext";
import SignedBonPhotoModal from "../../components/SignedBonPhotoModal";
import { LabelPrintModal } from "../bonLivraison/deliveryLabels";
import { CameraIcon } from "../../components/icons/InterfaceIcons";
import "./Livreur.css";
import {
    buildDeliveryOwnerKey,
    createDeliveryUploadFormData,
    enqueueDeliveryUpload,
    isRetryableUploadError,
    listDeliveryUploads,
    mergeQueuedDeliveries,
    readDeliveryCache,
    removeDeliveryUpload,
    saveDeliveryCache,
    updateDeliveryUpload,
} from "./offlineDeliveryQueue";

/* ─── Helpers ──────────────────────────────────────────────────── */
function getInitials(str) {
    return (str || "").split(/\s+/).map(w => w[0]).join("").substring(0, 2).toUpperCase();
}

function getCounts(ehpad) {
    const medicaments = ehpad.medicaments || [];
    const residentKeys = new Set(medicaments.map(m => `${m.patientNom ?? m.nom ?? ""}|${m.patientPrenom ?? m.prenom ?? ""}`));
    return {
        patientCount: ehpad.patientCount ?? residentKeys.size,
        productCount: ehpad.productCount ?? medicaments.length,
        unitCount: ehpad.unitCount ?? medicaments.reduce((acc, m) => acc + (m.quantite || 0), 0),
    };
}

function normalizeBoxCount(value) {
    const numeric = Number.parseInt(value, 10);
    if (!Number.isFinite(numeric)) return 1;
    return Math.max(1, Math.min(numeric, 999));
}

function normalizeLivreurBon(item) {
    return {
        ...item,
        nombreCaisses: normalizeBoxCount(item?.nombreCaisses),
    };
}

function displayBoxCount(value) {
    return value === "" ? "—" : normalizeBoxCount(value);
}

function localDateKey(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function isDeliveryScheduledToday(delivery) {
    return String(delivery?.deliveryOccurrenceDate || "").slice(0, 10) === localDateKey();
}

const DELIVERY_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

function formatDeliveryOccurrenceDate(value) {
    if (!value) return "";
    const match = DELIVERY_DATE_PATTERN.exec(String(value));
    if (!match) return String(value);
    const [, year, month, day] = match;
    return new Intl.DateTimeFormat("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "UTC",
    }).format(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))));
}

function DeliverySchedule({ delivery }) {
    const label = delivery?.deliveryPlanningLabel;
    const date = formatDeliveryOccurrenceDate(delivery?.deliveryOccurrenceDate);
    const time = delivery?.deliveryTime ? String(delivery.deliveryTime).slice(0, 5) : "";
    const parts = [label, date, time].filter(Boolean);
    const datePartIndex = label ? 1 : 0;

    if (parts.length === 0) return null;

    return (
        <span style={{ display: "block", fontSize: "12px", color: "#0C3B3C", marginTop: "4px", fontWeight: 800 }}>
            {parts.map((part, index) => {
                const content = date && index === datePartIndex
                    ? <time dateTime={delivery.deliveryOccurrenceDate}>{part}</time>
                    : part;
                return (
                    <React.Fragment key={`${part}-${index}`}>
                        {index > 0 && <span aria-hidden="true"> · </span>}
                        {content}
                    </React.Fragment>
                );
            })}
        </span>
    );
}

const MAX_SIGNED_BON_BATCH_PHOTOS = 8;
const MAX_SIGNED_BON_PHOTO_BYTES = 30 * 1024 * 1024;
const MAX_SIGNED_BON_TOTAL_BYTES = 180 * 1024 * 1024;
const PHOTO_FRAME_CORNERS = [
    { id: "top-left", style: { top: "16px", left: "16px", borderTop: "3px solid white", borderLeft: "3px solid white" } },
    { id: "top-right", style: { top: "16px", right: "16px", borderTop: "3px solid white", borderRight: "3px solid white" } },
    { id: "bottom-left", style: { bottom: "16px", left: "16px", borderBottom: "3px solid white", borderLeft: "3px solid white" } },
    { id: "bottom-right", style: { bottom: "16px", right: "16px", borderBottom: "3px solid white", borderRight: "3px solid white" } },
];

function formatMo(bytes) {
    return Math.round(bytes / 1024 / 1024);
}

function uploadErrorMessage(error) {
    if (error?.response?.status === 413) {
        return "Les photos sont trop lourdes. Ajoute moins de pages ou reprends des photos moins volumineuses, puis réessaie.";
    }
    return error?.response?.data?.message || "Erreur lors de la confirmation de la livraison. Veuillez réessayer.";
}

/* ─── Styles ───────────────────────────────────────────────────── */
const S = {
    page: {
        minHeight: "100%",
        background: "#F4F1EA",
        display: "flex",
        flexDirection: "column",
        maxWidth: "720px",
        margin: "0 auto",
        color: "#122A26",
        fontFamily: '"Manrope", Arial, Helvetica, sans-serif',
        boxShadow: "0 0 0 1px rgba(12, 59, 60, 0.08), 0 20px 60px rgba(12, 59, 60, 0.08)",
    },
    header: {
        background: "rgba(255, 255, 255, 0.96)",
        borderBottom: "1px solid #D8D2C6",
        padding: "18px 24px",
        display: "flex",
        alignItems: "center",
        gap: "12px",
        flexShrink: 0,
        position: "sticky",
        top: 0,
        zIndex: 10,
    },
    backBtn: {
        background: "none",
        border: "none",
        cursor: "pointer",
        color: "#0C3B3C",
        fontSize: "14px",
        fontWeight: 800,
        padding: "7px 0",
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
    ehpadCard: (delivered) => ({
        background: "white",
        border: `1px solid ${delivered ? "#D8D2C6" : "#0C3B3C"}`,
        borderRadius: "12px",
        padding: "16px",
        marginBottom: "12px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "14px",
        opacity: 1,
        boxShadow: delivered ? "none" : "0 8px 20px rgba(12, 59, 60, 0.10)",
        transition: "box-shadow 0.15s ease, transform 0.15s ease",
    }),
    ehpadAvatar: (color) => ({
        width: "42px",
        height: "42px",
        borderRadius: "50%",
        background: color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 800,
        fontSize: "13px",
        color: "#0C3B3C",
        flexShrink: 0,
        letterSpacing: "0.05em",
        border: "1px solid rgba(12, 59, 60, 0.22)",
    }),
    badge: (bg, color) => ({
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "7px",
        background: bg,
        color,
        fontSize: "11px",
        fontWeight: 800,
    }),
    stat: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minWidth: "52px",
    },
    statValue: {
        fontSize: "18px",
        fontWeight: 700,
        color: "#122A26",
        lineHeight: 1.2,
    },
    statLabel: {
        fontSize: "10px",
        color: "#5A6D68",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        fontWeight: 600,
    },
    photoBtn: {
        width: "100%",
        minHeight: "54px",
        padding: "15px 18px",
        background: "#17B978",
        color: "#0C3B3C",
        border: "none",
        borderRadius: "14px",
        fontSize: "16px",
        fontWeight: 800,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
    },
    confirmBtn: {
        flex: 1,
        minHeight: "50px",
        padding: "14px 18px",
        background: "#17B978",
        color: "#0C3B3C",
        border: "none",
        borderRadius: "13px",
        fontSize: "15px",
        fontWeight: 800,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
    },
    retakeBtn: {
        flex: 1,
        padding: "14px",
        background: "white",
        color: "#0C3B3C",
        border: "1px solid #D8D2C6",
        borderRadius: "13px",
        fontSize: "15px",
        fontWeight: 600,
        cursor: "pointer",
    },
    manualValidateBtn: {
        padding: "8px 12px",
        background: "#E7F7EF",
        color: "#0C3B3C",
        border: "1px solid #17B978",
        borderRadius: "9px",
        fontSize: "13px",
        fontWeight: 700,
        cursor: "pointer",
        whiteSpace: "nowrap",
    },
    fixedActions: {
        position: "sticky",
        bottom: 0,
        zIndex: 20,
        flexShrink: 0,
        padding: "12px 24px calc(12px + env(safe-area-inset-bottom, 0px))",
        margin: "0 -24px -20px",
        background: "rgba(255, 255, 255, 0.96)",
        borderTop: "1px solid #D8D2C6",
        boxShadow: "0 -8px 24px rgba(12, 59, 60, 0.06)",
        backdropFilter: "blur(8px)",
    },
    driverComment: {
        marginTop: "10px",
        padding: "7px 0 7px 10px",
        borderLeft: "3px solid #C8A44D",
        color: "#5A6D68",
        fontSize: "12px",
        lineHeight: 1.4,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
    },
    driverCommentLabel: {
        display: "block",
        marginBottom: "2px",
        color: "#7C6224",
        fontSize: "10px",
        fontWeight: 800,
        textTransform: "uppercase",
    },
    caisseInput: {
        width: "100%",
        boxSizing: "border-box",
        marginTop: "6px",
        padding: "10px 12px",
        border: "1px solid #B9C6C2",
        borderRadius: "12px",
        color: "#122A26",
        fontSize: "15px",
        fontWeight: 700,
        outline: "none",
    },
    detailTable: {
        width: "100%",
        borderCollapse: "collapse",
        tableLayout: "fixed",
        marginTop: "12px",
        fontSize: "12px",
    },
    detailTh: {
        padding: "8px 6px",
        color: "#5A6D68",
        textAlign: "left",
        fontSize: "10px",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        borderBottom: "1px solid #D8D2C6",
    },
    detailTd: {
        padding: "9px 6px",
        color: "#122A26",
        borderBottom: "1px solid #ECE8DF",
        verticalAlign: "top",
        overflowWrap: "anywhere",
    },
};

/* ─── Vue 1 : Liste des EHPADs ────────────────────────────────── */
function TourneeView({
    ehpads,
    onSelectEhpad,
    onViewPhoto,
    loading,
    onRefresh,
    canValidateManual,
    canPrintLabels,
    onValidateManual,
    validatingEhpadId,
    onPrintLabels,
    online,
    pendingUploads,
    syncing,
    onSync,
}) {
    const remaining = ehpads.filter(e => !e.delivered);
    const done = ehpads.filter(e => e.delivered);
    const completion = ehpads.length > 0 ? Math.round((done.length / ehpads.length) * 100) : 0;

    const today = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
    const todayCapitalized = today.charAt(0).toUpperCase() + today.slice(1);
    const pendingUploadMessage = `${pendingUploads.length} envoi${pendingUploads.length === 1 ? "" : "s"} en attente de synchronisation.`;
    const connectivityMessage = online
        ? pendingUploadMessage
        : "Hors connexion — les photos seront conservées sur cet appareil.";

    return (
        <div style={S.page}>
            <div className="livreur-page-header">
                <div className="livreur-page-header__top">
                    <div>
                        <div className="livreur-eyebrow">{todayCapitalized}</div>
                        <h1 className="livreur-title">Mes livraisons</h1>
                    </div>
                    <button onClick={onRefresh} className="livreur-refresh" title="Actualiser" aria-label="Actualiser">
                        ↻
                    </button>
                </div>
                {ehpads.length > 0 && (
                    <>
                        <div className="livreur-progress-meta">
                            <span>{remaining.length} EHPAD{remaining.length > 1 ? "s" : ""} restant{remaining.length > 1 ? "s" : ""} à livrer</span>
                            <strong>{done.length}/{ehpads.length}</strong>
                        </div>
                        <div className="livreur-progress-track" aria-hidden="true">
                            <div className="livreur-progress-bar" style={{ width: `${completion}%` }} />
                        </div>
                    </>
                )}
            </div>

            <div className="livreur-content">
                {(!online || pendingUploads.length > 0) && (
                    <div style={{
                        marginBottom: "14px",
                        padding: "12px 14px",
                        borderRadius: "10px",
                        border: `1px solid ${online ? "#86efac" : "#C8A44D"}`,
                        background: online ? "#f0fdf4" : "#fffaf0",
                        color: "#0C3B3C",
                        fontSize: "13px",
                        lineHeight: 1.4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                    }}>
                        <span>{connectivityMessage}</span>
                        {online && pendingUploads.length > 0 && (
                            <button
                                type="button"
                                onClick={onSync}
                                disabled={syncing}
                                style={{ ...S.manualValidateBtn, background: "#0C3B3C", color: "#F4F1EA", borderColor: "#0C3B3C" }}
                            >
                                {syncing ? "Envoi…" : "Synchroniser"}
                            </button>
                        )}
                    </div>
                )}
                {loading && (
                    <div style={{ textAlign: "center", padding: "48px", color: "#94a3b8", fontSize: "14px" }}>
                        Chargement…
                    </div>
                )}

                {!loading && ehpads.length === 0 && (
                    <div style={{ textAlign: "center", padding: "48px", color: "#94a3b8", fontSize: "14px" }}>
                        Aucune livraison en cours.
                    </div>
                )}

                {!loading && ehpads.length > 0 && (
                    <>
                        <div className="livreur-section-heading">
                            <span className="livreur-section-label">À livrer</span>
                            <span className="livreur-section-count">{remaining.length}</span>
                        </div>

                        {remaining.map((ehpad, idx) => (
                            <EhpadListItem
                                key={ehpad.deliveryHistoryId ?? ehpad.ehpadId}
                                ehpad={ehpad}
                                index={idx + 1}
                                delivered={false}
                                onClick={() => onSelectEhpad(ehpad)}
                                canValidateManual={canValidateManual}
                                onValidateManual={() => onValidateManual(ehpad)}
                                validating={validatingEhpadId === (ehpad.deliveryHistoryId ?? ehpad.ehpadId)}
                                canPrintLabels={canPrintLabels}
                                onPrintLabels={() => onPrintLabels(ehpad)}
                            />
                        ))}

                        {done.length > 0 && (
                            <>
                                <div className="livreur-section-heading" style={{ marginTop: "22px" }}>
                                    <span className="livreur-section-label">Livrés</span>
                                    <span className="livreur-section-count">{done.length}</span>
                                </div>
                                {done.map((ehpad, idx) => (
                                    <EhpadListItem
                                        key={ehpad.deliveryHistoryId ?? ehpad.ehpadId}
                                        ehpad={ehpad}
                                        index={remaining.length + idx + 1}
                                        delivered
                                        onClick={() => onViewPhoto(ehpad)}
                                        onViewPhoto={() => onViewPhoto(ehpad)}
                                    />
                                ))}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

function PrintLabelsButton({ onPrintLabels }) {
    return (
        <button
            type="button"
            title="Réimprimer les étiquettes caisses"
            onClick={event => { event.stopPropagation(); onPrintLabels(); }}
            style={{ ...S.manualValidateBtn, background: "#ccfbf1", color: "#0f766e", borderColor: "#14b8a6" }}
        >
            Réimprimer
        </button>
    );
}

function DeliveredEhpadActions({ ehpad, onViewPhoto, canPrintLabels, onPrintLabels }) {
    let status = <span style={S.badge("#dcfce7", "#166534")}>✓ Livré</span>;
    if (ehpad.pendingSync) {
        const background = ehpad.syncBlocked ? "#fef2f2" : "#fff7ed";
        const color = ehpad.syncBlocked ? "#b91c1c" : "#9a3412";
        const label = ehpad.syncBlocked ? "! Envoi à vérifier" : "↻ En attente d’envoi";
        status = <span style={S.badge(background, color)}>{label}</span>;
    }
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {status}
            {!ehpad.pendingSync && ehpad.deliveryHistoryId > 0 && onViewPhoto && (
                <button
                    type="button"
                    onClick={event => { event.stopPropagation(); onViewPhoto(); }}
                    style={{ ...S.manualValidateBtn, background: "#eef2ff", color: "#4338ca", borderColor: "#818cf8" }}
                >
                    Photos ({ehpad.signedBonPhotoCount || 0})
                </button>
            )}
            {canPrintLabels && onPrintLabels && <PrintLabelsButton onPrintLabels={onPrintLabels} />}
        </div>
    );
}

function PendingEhpadActions({ canValidateManual, onValidateManual, validating, canPrintLabels, onPrintLabels }) {
    return (
        <>
            {canPrintLabels && onPrintLabels && <PrintLabelsButton onPrintLabels={onPrintLabels} />}
            {canValidateManual && (
                <button
                    onClick={(event) => { event.stopPropagation(); onValidateManual(); }}
                    disabled={validating}
                    style={{
                        ...S.manualValidateBtn,
                        opacity: validating ? 0.55 : 1,
                        cursor: validating ? "not-allowed" : "pointer",
                    }}
                >
                    {validating ? "Validation…" : "Valider"}
                </button>
            )}
            <span style={{ color: "#cbd5e1", fontSize: "18px" }}>›</span>
        </>
    );
}

function EhpadListActions({ ehpad, delivered, canValidateManual, onValidateManual, validating, onViewPhoto, canPrintLabels, onPrintLabels }) {
    if (delivered) {
        return <DeliveredEhpadActions ehpad={ehpad} onViewPhoto={onViewPhoto} canPrintLabels={canPrintLabels} onPrintLabels={onPrintLabels} />;
    }
    if (canValidateManual || canPrintLabels) {
        return <PendingEhpadActions canValidateManual={canValidateManual} onValidateManual={onValidateManual} validating={validating} canPrintLabels={canPrintLabels} onPrintLabels={onPrintLabels} />;
    }
    return (
        <>
            <span style={S.badge("#ede9fe", "#6d28d9")}>À livrer</span>
            <span style={{ color: "#cbd5e1", fontSize: "18px" }}>›</span>
        </>
    );
}

function EhpadListItem({
    ehpad,
    index,
    delivered,
    onClick,
    canValidateManual = false,
    onValidateManual = () => {},
    validating = false,
    onViewPhoto = null,
    canPrintLabels = false,
    onPrintLabels = null,
}) {
    const initials = getInitials(ehpad.ehpadNom);
    const { patientCount, productCount, unitCount } = getCounts(ehpad);

    return (
        <div
            className="livreur-card"
            style={S.ehpadCard(delivered)}
        >
            <button className="livreur-card__button" type="button" onClick={onClick} aria-label={`Ouvrir ${ehpad.ehpadNom}`} style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "14px", padding: 0, border: 0, background: "transparent", fontFamily: "inherit", textAlign: "left", cursor: "pointer" }}>
                <span className="livreur-card__index">
                    {index}
                </span>
                <span className="livreur-card__avatar" style={S.ehpadAvatar("#E7F7EF")}>
                    {initials}
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <span className="livreur-card__name">{ehpad.ehpadNom}</span>
                        <span className="livreur-card__code">
                            {ehpad.ehpadCode}
                        </span>
                    </span>
                    {ehpad.ehpadAddress && (
                        <span style={{ display: "block", fontSize: "12px", color: "#5A6D68", marginTop: "3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {ehpad.ehpadAddress}
                        </span>
                    )}
                    <DeliverySchedule delivery={ehpad} />
                    {ehpad.commentaireLivreur && (
                        <DriverComment text={ehpad.commentaireLivreur} />
                    )}
                    <span className="livreur-card__stats">
                        <StatPill value={patientCount} label="résid." />
                        <StatPill value={productCount} label="produits" />
                        <StatPill value={unitCount} label="unités" />
                        {ehpad.nombreCaisses != null && <StatPill value={ehpad.nombreCaisses} label="caisses" />}
                    </span>
                </span>
            </button>
            <div className="livreur-card__actions">
                <EhpadListActions
                    ehpad={ehpad}
                    delivered={delivered}
                    canValidateManual={canValidateManual}
                    onValidateManual={onValidateManual}
                    validating={validating}
                    onViewPhoto={onViewPhoto}
                    canPrintLabels={canPrintLabels}
                    onPrintLabels={onPrintLabels}
                />
            </div>
        </div>
    );
}

function StatPill({ value, label }) {
    return (
        <span className="livreur-stat-pill">
            <strong>{value}</strong> {label}
        </span>
    );
}

/* ─── Vue 2 : Détail EHPAD ─────────────────────────────────────── */
function EhpadDetailView({ ehpad, onBack, onPrendrePhoto, onNombreCaissesChange, canSeeOrderDetail }) {
    const { patientCount, productCount, unitCount } = getCounts(ehpad);
    const medicaments = ehpad.medicaments || [];

    return (
        <div style={S.page}>
            <div style={S.header}>
                <button style={S.backBtn} onClick={onBack}>
                    ‹ Tournée
                </button>
            </div>

            <div className="livreur-detail-hero">
                {/* En-tête EHPAD */}
                <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span className="livreur-card__code">{ehpad.ehpadCode}</span>
                    <span style={S.badge("#F8EDD0", "#70581D")}>● À livrer</span>
                </div>
                <h1 className="livreur-detail-title">{ehpad.ehpadNom}</h1>
                {ehpad.ehpadAddress && (
                    <div style={{ color: "#5A6D68", fontSize: "12px" }}>{ehpad.ehpadAddress}</div>
                )}
                <DeliverySchedule delivery={ehpad} />
                <div className="livreur-detail-stats">
                    {[
                        { label: "résidents", value: patientCount },
                        { label: "produits",  value: productCount },
                        { label: "unités",    value: unitCount },
                        { label: "caisses", value: displayBoxCount(ehpad.nombreCaisses) },
                    ].map((stat) => (
                        <div className="livreur-detail-stat" key={stat.label}>
                            <span style={S.statValue}>{stat.value}</span>
                            <span style={{ ...S.statLabel, display: "block", marginTop: "3px" }}>{stat.label}</span>
                        </div>
                    ))}
                </div>
                {ehpad.commentaireLivreur && (
                    <DriverComment text={ehpad.commentaireLivreur} />
                )}
            </div>

            <div className="livreur-detail-content" style={{ display: "flex", flexDirection: "column", gap: "0" }}>
                <div style={{ flex: 1, marginBottom: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                    <div className="livreur-panel">
                        <label htmlFor="nombre-caisses" style={{ display: "block", color: "#122A26", fontSize: "13px", fontWeight: 800 }}>
                            Nombre de caisses
                        </label>
                        <input
                            id="nombre-caisses"
                            type="number"
                            inputMode="numeric"
                            min="1"
                            max="999"
                            value={ehpad.nombreCaisses ?? ""}
                            onChange={event => onNombreCaissesChange(event.target.value)}
                            placeholder="1"
                            style={S.caisseInput}
                        />
                    </div>
                    {canSeeOrderDetail ? (
                        <div className="livreur-panel">
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "baseline" }}>
                                <h2 style={{ margin: 0, color: "#1e293b", fontSize: "15px", fontWeight: 800 }}>
                                    Détail de la commande
                                </h2>
                                <span style={{ color: "#64748b", fontSize: "12px", fontWeight: 700 }}>
                                    {medicaments.length} ligne{medicaments.length > 1 ? "s" : ""}
                                </span>
                            </div>
                            {medicaments.length === 0 ? (
                                <div style={{ color: "#94a3b8", fontSize: "13px", paddingTop: "12px" }}>
                                    Aucun produit dans cette livraison.
                                </div>
                            ) : (
                                <div style={{ overflowX: "auto" }}>
                                    <table style={S.detailTable}>
                                        <thead>
                                            <tr>
                                                <th style={{ ...S.detailTh, width: "28%" }}>Patient</th>
                                                <th style={S.detailTh}>Produit</th>
                                                <th style={{ ...S.detailTh, width: "54px", textAlign: "center" }}>Qté</th>
                                                <th style={{ ...S.detailTh, width: "76px" }}>Flux</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {medicaments.map(med => (
                                                <tr key={med.lineKey || med.id}>
                                                    <td style={S.detailTd}>
                                                        {[med.patientPrenom, med.patientNom].filter(Boolean).join(" ") || "—"}
                                                    </td>
                                                    <td style={S.detailTd}>
                                                        <strong style={{ color: "#1e293b" }}>{med.nom || "—"}</strong>
                                                        {med.dosage && <div style={{ color: "#94a3b8", fontSize: "11px", marginTop: "2px" }}>{med.dosage}</div>}
                                                    </td>
                                                    <td style={{ ...S.detailTd, textAlign: "center", fontWeight: 800 }}>{med.quantite ?? "—"}</td>
                                                    <td style={S.detailTd}>{med.destination || "—"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="livreur-panel" style={{ color: "#5A6D68", fontSize: "13px", lineHeight: 1.5 }}>
                            <span aria-hidden="true" style={{ marginRight: "8px", color: "#0C3B3C" }}>▢</span>
                            {" "}
                            Le détail nominatif des patients et des produits n'est pas affiché dans l'espace livreur.
                        </div>
                    )}
                </div>

                {/* Bouton photo */}
                <div className="livreur-fixed-actions" style={S.fixedActions}>
                    <button style={S.photoBtn} onClick={onPrendrePhoto}>
                        <CameraIcon size={21} />
                        Prendre en photo le bon signé
                    </button>
                </div>
            </div>
        </div>
    );
}

function DriverComment({ text }) {
    return (
        <span style={{ ...S.driverComment, display: "block" }}>
            <span style={S.driverCommentLabel}>Commentaire de la pharmacie</span>
            {text}
        </span>
    );
}

/* ─── Vue 3 & 4 : Capture + Aperçu photo ──────────────────────── */
function PhotoView({ ehpad, onBack, onConfirm, confirming, appendMode = false }) {
    const [capturedPhotos, setCapturedPhotos] = useState([]);
    const [replaceIndex, setReplaceIndex] = useState(null);
    const inputRef = useRef(null);
    const capturedPhotosRef = useRef([]);

    const openPhotoPicker = (source = "camera", index = null) => {
        setReplaceIndex(index);
        if (!inputRef.current) return;
        if (source === "camera") {
            inputRef.current.setAttribute("capture", "environment");
        } else {
            inputRef.current.removeAttribute("capture");
        }
        inputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_SIGNED_BON_PHOTO_BYTES) {
            alert(`Cette photo dépasse ${formatMo(MAX_SIGNED_BON_PHOTO_BYTES)} Mo. Reprends-la avec une qualité plus légère.`);
            e.target.value = "";
            return;
        }
        const nextTotalBytes = capturedPhotos.reduce((total, photo, index) => (
            total + (index === replaceIndex ? 0 : photo.file.size)
        ), file.size);
        if (nextTotalBytes > MAX_SIGNED_BON_TOTAL_BYTES) {
            alert(`Le bon signé dépasse ${formatMo(MAX_SIGNED_BON_TOTAL_BYTES)} Mo au total. Supprime une page ou reprends des photos moins lourdes.`);
            e.target.value = "";
            return;
        }
        if (replaceIndex === null && capturedPhotos.length >= MAX_SIGNED_BON_BATCH_PHOTOS) {
            alert(`Envoie ces ${MAX_SIGNED_BON_BATCH_PHOTOS} photos maintenant. Tu pourras ajouter d'autres pages au même bon signé ensuite si nécessaire.`);
            e.target.value = "";
            return;
        }
        const nextPhoto = { file, previewUrl: URL.createObjectURL(file) };
        setCapturedPhotos(current => {
            if (replaceIndex === null) return [...current, nextPhoto];
            return current.map((photo, index) => {
                if (index !== replaceIndex) return photo;
                URL.revokeObjectURL(photo.previewUrl);
                return nextPhoto;
            });
        });
        setReplaceIndex(null);
        e.target.value = "";
    };

    useEffect(() => {
        capturedPhotosRef.current = capturedPhotos;
    }, [capturedPhotos]);

    useEffect(() => {
        return () => {
            capturedPhotosRef.current.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
        };
    }, []);

    const hasPhotos = capturedPhotos.length > 0;
    let confirmationLabel = <>✓ Confirmer la livraison</>;
    if (appendMode) confirmationLabel = "Ajouter les photos";
    if (confirming) confirmationLabel = "Envoi…";

    return (
        <div style={S.page}>
            <div style={S.header}>
                <button style={S.backBtn} onClick={onBack}>‹ Détail</button>
            </div>

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />

            <div className="livreur-photo-content" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                <div>
                    <div className="livreur-photo-heading">
                        {hasPhotos ? "Bon de livraison capturé" : "Bon de livraison signé"}
                    </div>
                    <div style={{ fontSize: "13px", color: "#5A6D68", marginTop: "4px" }}>
                        {ehpad.ehpadNom} · {ehpad.ehpadCode}
                    </div>
                    {appendMode && (
                        <div style={{ fontSize: "12px", color: "#0f766e", fontWeight: 700, marginTop: "6px" }}>
                            Ces photos seront ajoutées au bon déjà livré.
                        </div>
                    )}
                </div>

                {!hasPhotos ? (
                    /* Zone d'attente photo */
                    <div
                        className="livreur-photo-dropzone"
                        style={{
                            flex: 1,
                            background: "#fff",
                            borderRadius: "16px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "12px",
                            cursor: "pointer",
                            minHeight: "300px",
                        }}
                    >
                        <span style={{ display: "grid", width: "58px", height: "58px", placeItems: "center", borderRadius: "50%", background: "#E7F7EF", color: "#0C3B3C" }}>
                            <CameraIcon size={28} />
                        </span>
                        <div style={{ color: "#122A26", fontSize: "14px", fontWeight: 800 }}>Ajouter une photo du bon signé</div>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center" }}>
                            <button type="button" style={{ ...S.confirmBtn, flex: "0 1 auto", padding: "12px 14px" }} onClick={() => openPhotoPicker("camera", null)}>
                                Prendre une photo
                            </button>
                            <button type="button" style={{ ...S.retakeBtn, flex: "0 1 auto", padding: "12px 14px" }} onClick={() => openPhotoPicker("gallery", null)}>
                                Choisir depuis la galerie
                            </button>
                        </div>
                        {/* Coins de cadrage */}
                        {PHOTO_FRAME_CORNERS.map(corner => (
                            <div key={corner.id} style={{ position: "absolute", width: "24px", height: "24px", opacity: 0.22, ...corner.style }} />
                        ))}
                    </div>
                ) : (
                    /* Aperçu photo */
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "16px" }}>
                        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "14px", paddingBottom: "4px" }}>
                            {capturedPhotos.map((photo, index) => (
                                <div className="livreur-photo-card" key={`${photo.file.name}-${index}`}>
                                    <div style={{ padding: "10px 12px", borderBottom: "1px solid #D8D2C6", display: "flex", alignItems: "center", gap: "10px" }}>
                                        <strong style={{ color: "#122A26", fontSize: "14px", flex: 1 }}>Page {index + 1}</strong>
                                        <button type="button" style={{ ...S.retakeBtn, flex: "0 0 auto", padding: "8px 10px", fontSize: "13px" }} onClick={() => openPhotoPicker("gallery", index)} disabled={confirming}>
                                            Modifier
                                        </button>
                                    </div>
                                    <div style={{
                                        background: "#F4F1EA",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        minHeight: "220px",
                                    }}>
                                        <img
                                            src={photo.previewUrl}
                                            alt={`Bon signé page ${index + 1}`}
                                            style={{ width: "100%", maxHeight: "360px", objectFit: "contain" }}
                                        />
                                    </div>
                                    <div style={{ fontSize: "12px", color: "#5A6D68", textAlign: "center", padding: "8px 10px" }}>
                                        {photo.file.name || `bon_livraison_page_${index + 1}.jpg`}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="livreur-fixed-actions livreur-photo-actions" style={{ ...S.fixedActions, marginBottom: "-20px" }}>
                            <button style={{ ...S.retakeBtn, flex: "1 1 150px" }} onClick={() => openPhotoPicker("camera", null)} disabled={confirming}>
                                Prendre une page
                            </button>
                            <button style={{ ...S.retakeBtn, flex: "1 1 170px" }} onClick={() => openPhotoPicker("gallery", null)} disabled={confirming}>
                                Ajouter depuis galerie
                            </button>
                            <button
                                style={{ ...S.confirmBtn, flex: "1 1 190px", opacity: confirming ? 0.6 : 1 }}
                                onClick={() => onConfirm(capturedPhotos.map(photo => photo.file))}
                                disabled={confirming}
                            >
                                {confirmationLabel}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─── Composant principal ──────────────────────────────────────── */
export default function Livreur() {
    const { user, selectedPharmacy, pharmacyId } = useAuth() || {};
    const [view, setView] = useState("list");
    const [ehpads, setEhpads] = useState([]);
    const [pendingUploads, setPendingUploads] = useState([]);
    const [online, setOnline] = useState(() => typeof navigator === "undefined" || navigator.onLine !== false);
    const [syncing, setSyncing] = useState(false);
    const [selectedEhpad, setSelectedEhpad] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [validatingEhpadId, setValidatingEhpadId] = useState(null);
    const [photoHistorique, setPhotoHistorique] = useState(null);
    const [labelTarget, setLabelTarget] = useState(null);
    const [pharmacy, setPharmacy] = useState(null);
    const syncInProgressRef = useRef(false);
    const canValidateManual = ["ADMIN", "SUPER_ADMIN", "PHARMACIEN"].includes(user?.role);
    const canSeeOrderDetail = ["ADMIN", "SUPER_ADMIN", "PHARMACIEN"].includes(user?.role);
    const canPrintLabels = ["TITULAIRE", "ADMIN", "SUPER_ADMIN", "PHARMACIEN"].includes(user?.role);
    const deliveryOwnerKey = buildDeliveryOwnerKey(user, pharmacyId ?? selectedPharmacy?.id);

    useEffect(() => {
        if (!canPrintLabels) return;
        API.get("/admin/pharmacy", { noRedirectOn401: true })
            .then(r => setPharmacy(r.data))
            .catch(() => {});
    }, [canPrintLabels, selectedPharmacy?.id]);

    const effectivePharmacy = pharmacy || selectedPharmacy;

    const refreshPendingUploads = useCallback(async () => {
        try {
            const uploads = await listDeliveryUploads(deliveryOwnerKey);
            setPendingUploads(uploads);
            return uploads;
        } catch (error) {
            console.error("Impossible de lire les livraisons hors connexion", error);
            return [];
        }
    }, [deliveryOwnerKey]);

    const fetchEhpads = useCallback(async () => {
        setLoading(true);
        try {
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
                throw new Error("offline");
            }
            const [pendingResponse, historyResponse] = await Promise.all([
                API.get("/bon-livraison/livreur", { noRedirectOn401: true }),
                API.get("/bon-livraison/livreur/historique-aujourdhui", { noRedirectOn401: true }),
            ]);
            const deliveredToday = (historyResponse.data || []).map(item => ({
                ...normalizeLivreurBon(item),
                deliveryHistoryId: item.id,
                delivered: true,
            }));
            const pending = (pendingResponse.data || []).map(item => ({
                ...normalizeLivreurBon(item),
                deliveryHistoryId: item.id,
                delivered: false,
            }));
            const deliveries = [...pending, ...deliveredToday];
            setEhpads(deliveries);
            await saveDeliveryCache(deliveryOwnerKey, deliveries);
            return deliveries;
        } catch (error) {
            const cachedDeliveries = await readDeliveryCache(deliveryOwnerKey).catch(() => []);
            if (cachedDeliveries.length > 0) setEhpads(cachedDeliveries);
            else if (error?.message !== "offline") console.error(error);
            return cachedDeliveries;
        } finally {
            setLoading(false);
        }
    }, [deliveryOwnerKey]);

    const postQueuedUpload = useCallback(upload => API.post(
        upload.url,
        createDeliveryUploadFormData(upload),
        {
            headers: { "Content-Type": "multipart/form-data" },
            noRedirectOn401: true,
        }
    ), []);

    const synchronizePendingUploads = useCallback(async (retryBlocked = false) => {
        if (syncInProgressRef.current || (typeof navigator !== "undefined" && navigator.onLine === false)) return;
        syncInProgressRef.current = true;
        setSyncing(true);
        try {
            const uploads = await listDeliveryUploads(deliveryOwnerKey);
            for (const upload of uploads) {
                if (upload.status === "blocked" && !retryBlocked) continue;
                try {
                    await postQueuedUpload(upload);
                    await removeDeliveryUpload(upload.id);
                } catch (error) {
                    if (!isRetryableUploadError(error)) {
                        await updateDeliveryUpload({
                            ...upload,
                            status: "blocked",
                            attempts: (upload.attempts || 0) + 1,
                            lastError: uploadErrorMessage(error),
                        });
                    }
                    if (isRetryableUploadError(error)) break;
                }
            }
            await refreshPendingUploads();
            await fetchEhpads();
        } finally {
            syncInProgressRef.current = false;
            setSyncing(false);
        }
    }, [deliveryOwnerKey, fetchEhpads, postQueuedUpload, refreshPendingUploads]);

    useEffect(() => {
        fetchEhpads();
        refreshPendingUploads().then(uploads => {
            if (uploads.some(upload => upload.status !== "blocked")) synchronizePendingUploads();
        });
    }, [fetchEhpads, refreshPendingUploads, synchronizePendingUploads]);

    useEffect(() => {
        const handleOnline = () => {
            setOnline(true);
            synchronizePendingUploads();
        };
        const handleOffline = () => setOnline(false);
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
        };
    }, [synchronizePendingUploads]);

    const handleSelectEhpad = (ehpad) => {
        setSelectedEhpad(ehpad);
        setView("detail");
    };

    const handleDeletePhotos = async (ehpad, pageIndexes) => {
        if (!ehpad?.deliveryHistoryId || !pageIndexes?.length) return ehpad?.signedBonPhotoCount || 0;
        try {
            await API.delete(
                `/bon-livraison/livreur/historique/${ehpad.deliveryHistoryId}/photos`,
                { data: { pageIndexes }, noRedirectOn401: true }
            );
            const remainingCount = Math.max(0, (ehpad.signedBonPhotoCount || 0) - pageIndexes.length);
            const updatePhotoCount = item => deliveryActionKey(item) === deliveryActionKey(ehpad)
                ? { ...item, signedBonPhotoCount: remainingCount, signedBonAvailable: remainingCount > 0 }
                : item;
            setEhpads(current => current.map(updatePhotoCount));
            setPhotoHistorique(current => current ? updatePhotoCount(current) : current);
            return remainingCount;
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Impossible de supprimer cette image. Veuillez réessayer.");
            throw error;
        }
    };

    const handleAddDeliveredPhotos = async (ehpad, files) => {
        if (!ehpad?.deliveryHistoryId || !files?.length) return;
        const totalBytes = files.reduce((total, file) => total + (file?.size || 0), 0);
        if (files.length > MAX_SIGNED_BON_BATCH_PHOTOS || totalBytes > MAX_SIGNED_BON_TOTAL_BYTES) {
            alert(`Ce lot est trop volumineux. Limite l'envoi à ${MAX_SIGNED_BON_BATCH_PHOTOS} photos et ${formatMo(MAX_SIGNED_BON_TOTAL_BYTES)} Mo.`);
            return;
        }
        const uploadDraft = {
            ownerKey: deliveryOwnerKey,
            kind: "append",
            url: deliveryAppendPhotosUrl(ehpad),
            nombreCaisses: normalizeBoxCount(ehpad.nombreCaisses),
            ehpad: { ...ehpad },
            files,
        };
        try {
            if (typeof navigator !== "undefined" && navigator.onLine === false) {
                await enqueueDeliveryUpload(uploadDraft);
                await refreshPendingUploads();
                setPhotoHistorique(null);
                alert("Photos enregistrées sur l'appareil. Elles seront envoyées automatiquement au retour du réseau.");
                return;
            }
            await postQueuedUpload(uploadDraft);
            const newCount = (ehpad.signedBonPhotoCount || 0) + files.length;
            const update = item => deliveryActionKey(item) === deliveryActionKey(ehpad)
                ? { ...item, signedBonPhotoCount: newCount, signedBonAvailable: true }
                : item;
            setEhpads(current => current.map(update));
            setPhotoHistorique(current => current ? update(current) : current);
        } catch (error) {
            console.error(error);
            alert(uploadErrorMessage(error));
            throw error;
        }
    };

    const deliveryActionKey = (ehpad) => ehpad?.deliveryHistoryId ?? ehpad?.ehpadId;
    const deliveryConfirmUrl = (ehpad) => ehpad?.deliveryHistoryId
        ? `/bon-livraison/livreur/historique/${ehpad.deliveryHistoryId}/confirmer`
        : `/bon-livraison/livreur/${ehpad.ehpadId}/confirmer`;
    const deliveryAppendPhotosUrl = (ehpad) => `/bon-livraison/livreur/historique/${ehpad.deliveryHistoryId}/photos`;
    const deliveryBoxCountUrl = (ehpad) => ehpad?.deliveryHistoryId
        ? `/bon-livraison/livreur/historique/${ehpad.deliveryHistoryId}/nombre-caisses`
        : `/bon-livraison/livreur/${ehpad.ehpadId}/nombre-caisses`;

    const closeConfirmedDelivery = () => {
        setSelectedEhpad(null);
        setView("list");
    };

    const queueOfflineConfirmation = async (uploadDraft) => {
        const upload = await enqueueDeliveryUpload(uploadDraft);
        await refreshPendingUploads();
        if (!upload.persistent) {
            alert("Le stockage hors connexion n'est pas disponible sur cet appareil. Laisse cette page ouverte jusqu'au retour du réseau.");
            return;
        }
        closeConfirmedDelivery();
        alert("Photos enregistrées sur l'appareil. Elles seront envoyées automatiquement au retour du réseau.");
    };

    const preserveRetryableUpload = async (uploadDraft, error) => {
        try {
            const upload = await enqueueDeliveryUpload(uploadDraft);
            await updateDeliveryUpload({
                ...upload,
                attempts: 1,
                lastError: uploadErrorMessage(error),
            });
            await refreshPendingUploads();
            closeConfirmedDelivery();
            alert("Le réseau est indisponible. Les photos sont conservées sur cet appareil et seront renvoyées automatiquement.");
        } catch (storageError) {
            console.error(storageError);
            alert("L'envoi a échoué et le stockage hors connexion est indisponible. Les photos restent affichées : réessaie sans fermer cette page.");
        }
    };

    const uploadConfirmationOnline = async (uploadDraft) => {
        try {
            // En ligne, l'upload ne doit jamais dépendre d'IndexedDB : certains
            // navigateurs mobiles l'exposent mais refusent ou bloquent l'écriture.
            await postQueuedUpload(uploadDraft);
            await fetchEhpads();
            closeConfirmedDelivery();
        } catch (error) {
            console.error(error);
            if (isRetryableUploadError(error)) {
                await preserveRetryableUpload(uploadDraft, error);
                return;
            }
            alert(uploadErrorMessage(error));
        }
    };

    const handleConfirm = async (photoFiles) => {
        if (!selectedEhpad) return;
        setConfirming(true);
        try {
            const files = Array.isArray(photoFiles) ? photoFiles : [photoFiles].filter(Boolean);
            const totalBytes = files.reduce((total, file) => total + (file?.size || 0), 0);
            if (files.length > MAX_SIGNED_BON_BATCH_PHOTOS || totalBytes > MAX_SIGNED_BON_TOTAL_BYTES) {
                alert(`Ce lot est trop volumineux. Limite l'envoi à ${MAX_SIGNED_BON_BATCH_PHOTOS} photos et ${formatMo(MAX_SIGNED_BON_TOTAL_BYTES)} Mo, puis ajoute un autre lot si nécessaire.`);
                return;
            }
            const uploadDraft = {
                ownerKey: deliveryOwnerKey,
                kind: selectedEhpad.delivered ? "append" : "confirm",
                url: selectedEhpad.delivered ? deliveryAppendPhotosUrl(selectedEhpad) : deliveryConfirmUrl(selectedEhpad),
                nombreCaisses: normalizeBoxCount(selectedEhpad.nombreCaisses),
                ehpad: { ...selectedEhpad },
                files,
            };

            if (typeof navigator !== "undefined" && navigator.onLine === false) {
                await queueOfflineConfirmation(uploadDraft);
                return;
            }
            await uploadConfirmationOnline(uploadDraft);
        } catch (err) {
            console.error(err);
            alert("Impossible d'enregistrer les photos sur cet appareil. Veuillez laisser cette page ouverte et réessayer.");
        } finally {
            setConfirming(false);
        }
    };

    const handleManualValidate = async (ehpad) => {
        if (!ehpad || !canValidateManual) return;
        setValidatingEhpadId(deliveryActionKey(ehpad));
        try {
            const formData = new FormData();
            formData.append("nombreCaisses", String(normalizeBoxCount(ehpad.nombreCaisses)));
            await API.post(deliveryConfirmUrl(ehpad), formData, { noRedirectOn401: true });
            await fetchEhpads();
            if (deliveryActionKey(selectedEhpad) === deliveryActionKey(ehpad)) {
                setSelectedEhpad(null);
                setView("list");
            }
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la validation de la livraison. Veuillez réessayer.");
        } finally {
            setValidatingEhpadId(null);
        }
    };

    const updateLocalNombreCaisses = useCallback((targetKey, value) => {
        setEhpads(current => current.map(ehpad => (
            (ehpad.deliveryHistoryId ?? ehpad.ehpadId) === targetKey ? { ...ehpad, nombreCaisses: value } : ehpad
        )));
        setSelectedEhpad(current => deliveryActionKey(current) === targetKey ? { ...current, nombreCaisses: value } : current);
        setLabelTarget(current => deliveryActionKey(current) === targetKey ? { ...current, nombreCaisses: value } : current);
    }, []);

    const handleNombreCaissesChange = (value) => {
        if (!selectedEhpad) return;
        updateLocalNombreCaisses(deliveryActionKey(selectedEhpad), value);
    };

    const handleLabelPrintSettingsSave = async ({ boxCount }) => {
        if (!labelTarget) return;
        const normalized = normalizeBoxCount(boxCount);
        await API.patch(
            deliveryBoxCountUrl(labelTarget),
            { nombreCaisses: normalized },
            { noRedirectOn401: true }
        );
        updateLocalNombreCaisses(deliveryActionKey(labelTarget), normalized);
    };

    const mergedEhpads = mergeQueuedDeliveries(ehpads, pendingUploads);
    const displayedEhpads = user?.role === "LIVREUR"
        ? mergedEhpads.filter(isDeliveryScheduledToday)
        : mergedEhpads;

    return (
        <div className="livreur-shell" style={{ flex: 1, overflowY: "auto" }}>
            {view === "list" && (
                <TourneeView
                    ehpads={displayedEhpads}
                    onSelectEhpad={handleSelectEhpad}
                    onViewPhoto={setPhotoHistorique}
                    loading={loading}
                    onRefresh={fetchEhpads}
                    canValidateManual={canValidateManual}
                    canPrintLabels={canPrintLabels}
                    onValidateManual={handleManualValidate}
                    validatingEhpadId={validatingEhpadId}
                    onPrintLabels={setLabelTarget}
                    online={online}
                    pendingUploads={pendingUploads}
                    syncing={syncing}
                    onSync={() => synchronizePendingUploads(true)}
                />
            )}
            {view === "detail" && selectedEhpad && (
                <EhpadDetailView
                    ehpad={selectedEhpad}
                    onBack={() => setView("list")}
                    onPrendrePhoto={() => setView("photo")}
                    onNombreCaissesChange={handleNombreCaissesChange}
                    canSeeOrderDetail={canSeeOrderDetail}
                />
            )}
            {view === "photo" && selectedEhpad && (
                <PhotoView
                    ehpad={selectedEhpad}
                    onBack={() => setView("detail")}
                    onConfirm={handleConfirm}
                    confirming={confirming}
                    appendMode={Boolean(selectedEhpad.delivered)}
                />
            )}
            {photoHistorique && (
                <SignedBonPhotoModal
                    historiqueId={photoHistorique.deliveryHistoryId}
                    title={`Bon signé — ${photoHistorique.ehpadNom}`}
                    photoCount={photoHistorique.signedBonPhotoCount}
                    onClose={() => setPhotoHistorique(null)}
                    onAddFiles={files => handleAddDeliveredPhotos(photoHistorique, files)}
                    onDeleteMany={pageIndexes => handleDeletePhotos(photoHistorique, pageIndexes)}
                />
            )}
            {labelTarget && (
                <LabelPrintModal
                    ehpad={labelTarget}
                    pharmacy={effectivePharmacy}
                    initialBoxCount={labelTarget.nombreCaisses ?? 1}
                    onPrintSettingsSave={handleLabelPrintSettingsSave}
                    onClose={() => setLabelTarget(null)}
                />
            )}
        </div>
    );
}
